import React from "react";
import { ArrowRight, Lock } from "lucide-react";
import { GUIDE } from "../mock";
import { Reveal } from "../hooks/useReveal";

const CTA = ({ className = "" }) => (
  <a
    href={GUIDE.checkoutUrl}
    target="_blank"
    rel="noreferrer"
    className={`group inline-flex items-center gap-2.5 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] text-white font-medium px-8 py-4 transition-all duration-300 shadow-lg shadow-[#D2703F]/30 hover:-translate-y-0.5 ${className}`}
  >
    Access the Digital Guide
    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

const Guide = () => {
  return (
    <section id="guide" className="bg-[#EFE9DD] scroll-mt-20">
      {/* Intro / offer */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Reveal className="flex items-center justify-center gap-4 mb-14">
          <span className="h-px w-10 bg-[#D2703F]" />
          <span className="eyebrow text-[#B85A2C]">{GUIDE.eyebrow}</span>
          <span className="h-px w-10 bg-[#D2703F]" />
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Cover image */}
          <Reveal className="flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-[#D2703F]/15 blur-xl" />
              <img
                src={GUIDE.cover}
                alt="SIQ Adventures Digital DIY Guide cover"
                className="relative w-full max-w-sm rounded-2xl shadow-2xl shadow-[#26302A]/25 border border-white/60"
                loading="lazy"
              />
            </div>
          </Reveal>

          {/* Offer text */}
          <Reveal delay={120} className="text-center lg:text-left">
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] text-[#26302A]">
              {GUIDE.title}
            </h2>
            <p className="mt-5 font-serif-display italic text-2xl md:text-3xl text-[#B85A2C]">
              {GUIDE.subtitle}
            </p>
            <p className="mt-5 text-[#26302A]/70 text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              {GUIDE.madeForIntro}
            </p>
            <ul className="mt-4 space-y-2 max-w-xl mx-auto lg:mx-0 text-left">
              {GUIDE.madeForBullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[#26302A]/75 text-[15px] leading-snug">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D2703F] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[#26302A]/70 text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              {GUIDE.madeForClosing}
            </p>
            <div className="mt-9 flex flex-col items-center lg:items-start gap-5">
              <span className="font-serif-display text-4xl text-[#B85A2C]">{GUIDE.price}</span>
              <CTA />
            </div>
          </Reveal>
        </div>

        <Reveal delay={160}>
          <p className="mt-16 font-serif-display italic text-2xl md:text-3xl text-[#26302A]/80 max-w-3xl mx-auto leading-snug text-center">
            “{GUIDE.tagline}”
          </p>
        </Reveal>
      </div>

      {/* A peek at the pages */}
      <div className="bg-[#24322B] py-24 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <Reveal className="text-center mb-14">
            <span className="eyebrow text-[#D2703F]">What's Inside</span>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#F4EFE6] mt-4">
              A peek at the pages
            </h3>
          </Reveal>

          {/* Blurred page previews with crisp headings */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {GUIDE.screenshots.map((page, i) => (
              <Reveal key={page.src} delay={(i % 3) * 100}>
                <div className="group relative overflow-hidden rounded-2xl border border-[#F4EFE6]/10 bg-black aspect-[3/4]">
                  <img
                    src={page.src}
                    alt={page.heading}
                    className="absolute inset-0 w-full h-full object-cover blur-[7px] scale-110 opacity-70"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2621]/85 via-[#1C2621]/25 to-transparent" />
                  <div className="absolute inset-0 flex flex-col items-center justify-end p-5 text-center">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-[#D2703F]/90 text-white mb-auto mt-4">
                      <Lock className="w-4 h-4" />
                    </span>
                    <h4 className="font-serif-display text-xl md:text-2xl text-[#F4EFE6] leading-tight drop-shadow">
                      {page.heading}
                    </h4>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center mt-12 flex flex-col items-center gap-5">
            <CTA />
            <p className="text-[#F4EFE6]/55 text-sm max-w-md">
              Secure checkout opens in a new tab. A portion of every guide purchase supports community projects on the island.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Guide;
